import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { getToolLibrary, submitSystemMapper, getSystemMapperHistory, downloadSystemMapperPDF } from "@/lib/api";
import { AbsolinDisclaimer } from "@/components/AbsolinDisclaimer";
import {
  ArrowRight, ArrowLeft, Loader2, Network, AlertTriangle, Download, History, AlertCircle,
  IndianRupee, ShoppingCart, Package, Factory, TrendingUp, Users, ShieldCheck, Truck,
  Monitor, Headphones, CheckCircle, XCircle, Minus, ArrowRightLeft
} from "lucide-react";

const DEPT_ICONS = {
  finance: IndianRupee, hr: Users, sales: TrendingUp,
  inventory: Package, production: Factory, procurement: ShoppingCart,
  quality: ShieldCheck, logistics: Truck, it: Monitor, support: Headphones
};

const DEPT_COLORS = {
  finance: "#0066CC", hr: "#0891B2", sales: "#DC2626",
  inventory: "#D97706", production: "#7C3AED", procurement: "#059669",
  quality: "#4F46E5", logistics: "#EA580C", it: "#6366F1", support: "#14B8A6"
};

const CONNECTION_STYLES = {
  "API / Integrated": { color: "#10B981", label: "Integrated", dash: false },
  "File transfer (Excel/CSV)": { color: "#F59E0B", label: "File Transfer", dash: true },
  "Email": { color: "#F59E0B", label: "Email", dash: true },
  "WhatsApp / Verbal": { color: "#EF4444", label: "Verbal/WhatsApp", dash: true },
  "Manual re-entry": { color: "#EF4444", label: "Manual", dash: true },
  "No sharing": { color: "#94A3B8", label: "No Sharing", dash: false },
};

export default function SystemMapperPage() {
  const [step, setStep] = useState("landing");
  const [toolLibrary, setToolLibrary] = useState({ departments: [], sharing_methods: [] });
  const [currentDeptIdx, setCurrentDeptIdx] = useState(0);
  const [mappings, setMappings] = useState({});
  const [contactInfo, setContactInfo] = useState({ company_name: "", industry: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { loadLibrary(); }, []);

  const loadLibrary = async () => {
    try { const res = await getToolLibrary(); setToolLibrary(res.data); }
    catch { toast.error("Failed to load tool library"); }
  };

  const loadHistory = async (email) => {
    if (!email) return;
    setHistoryLoading(true);
    try { const res = await getSystemMapperHistory(email); setHistory(res.data.landscapes || []); }
    catch { /* no history */ }
    finally { setHistoryLoading(false); }
  };

  const downloadReport = async (reportData) => {
    const r = reportData || result;
    if (!r) return;
    try {
      toast.info("Generating PDF report...");
      const res = await downloadSystemMapperPDF(r.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SystemLandscape_${r.company_name.replace(/\s+/g, '_')}_${new Date(r.created_at).toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const maxReached = history.length >= 3;

  const departments = toolLibrary.departments;
  const currentDept = departments[currentDeptIdx];
  const currentMapping = mappings[currentDept?.key] || {};

  const updateMapping = (key, value) => {
    setMappings(prev => ({
      ...prev,
      [currentDept.key]: { ...(prev[currentDept.key] || {}), [key]: value }
    }));
  };

  const toggleShareWith = (deptKey) => {
    const current = currentMapping.shares_data_with || [];
    const updated = current.includes(deptKey) ? current.filter(k => k !== deptKey) : [...current, deptKey];
    updateMapping("shares_data_with", updated);
  };

  const mappedCount = Object.keys(mappings).filter(k => mappings[k]?.primary_tool).length;
  const progress = departments.length > 0 ? Math.round((mappedCount / departments.length) * 100) : 0;

  const handleSubmit = async () => {
    if (!contactInfo.company_name || !contactInfo.contact_name || !contactInfo.contact_email) {
      toast.error("Please fill required fields"); return;
    }
    setLoading(true);
    try {
      const deptList = departments.map(d => ({
        department_key: d.key,
        primary_tool: mappings[d.key]?.primary_tool || "No system",
        secondary_tools: mappings[d.key]?.secondary_tools || [],
        shares_data_with: mappings[d.key]?.shares_data_with || [],
        sharing_method: mappings[d.key]?.sharing_method || "No sharing"
      }));
      const res = await submitSystemMapper({ ...contactInfo, departments: deptList });
      setResult(res.data);
      setStep("results");
      loadHistory(contactInfo.contact_email);
    } catch { toast.error("Failed to submit"); }
    finally { setLoading(false); }
  };

  // LANDING
  if (step === "landing") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="pt-24 pb-16 flex-1">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full text-xs text-indigo-700 font-semibold uppercase tracking-wider mb-6">
                <Network className="w-3.5 h-3.5" /> IT Audit Tool
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" data-testid="mapper-title">
                System Landscape
                <span className="text-indigo-600"> Mapper</span>
              </h1>
              <p className="text-slate-500 text-base sm:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
                Map your current software across all departments. See the disconnection. Understand why your data lives in 8 different places and nobody has the complete picture.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-10">
              {departments.slice(0, 10).map((d) => {
                const Icon = DEPT_ICONS[d.key] || Monitor;
                return (
                  <div key={d.key} className="flex flex-col items-center p-3 rounded-xl border border-slate-200 hover:border-indigo-200 transition-colors">
                    <Icon className="w-6 h-6 mb-2" style={{ color: DEPT_COLORS[d.key] }} />
                    <span className="text-xs text-center font-medium text-slate-700">{d.name}</span>
                  </div>
                );
              })}
            </div>

            <div className="text-center">
              <Button size="lg" onClick={() => setStep("mapper")}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-12 text-sm font-semibold rounded-xl"
                data-testid="start-mapper-btn">
                Map Your IT Landscape <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-slate-400 text-xs mt-3">10 departments. Select tools and connections. Get your Integration Score.</p>
            </div>
          </div>
        </div>
        <AbsolinDisclaimer />
        <Footer />
      </div>
    );
  }

  // MAPPER FORM
  if (step === "mapper" && currentDept) {
    const Icon = DEPT_ICONS[currentDept.key] || Monitor;
    const color = DEPT_COLORS[currentDept.key];
    const isLast = currentDeptIdx === departments.length - 1;

    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-4">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-slate-500 font-medium">{mappedCount} of {departments.length} departments mapped</span>
              <span className="text-xs font-bold text-indigo-600">{progress}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {/* Department tabs */}
          <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
            {departments.map((d, i) => {
              const hasTool = !!mappings[d.key]?.primary_tool;
              return (
                <button key={d.key} onClick={() => setCurrentDeptIdx(i)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                    i === currentDeptIdx ? "text-white" : hasTool ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  style={i === currentDeptIdx ? { backgroundColor: color } : {}}>
                  {d.name.split(" ")[0]}
                  {hasTool && i !== currentDeptIdx && <CheckCircle className="w-3 h-3 inline ml-1" />}
                </button>
              );
            })}
          </div>

          <Card className="border-t-4" style={{ borderTopColor: color }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <Icon className="w-6 h-6" style={{ color }} />
                <h2 className="text-lg font-bold text-slate-900">{currentDept.name}</h2>
              </div>

              {/* Primary Tool */}
              <div className="mb-5">
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Primary Tool / System *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {currentDept.tools.map((tool) => (
                    <button key={tool} onClick={() => updateMapping("primary_tool", tool)}
                      data-testid={`tool-${currentDept.key}-${tool.replace(/\s+/g, '-').toLowerCase()}`}
                      className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${
                        currentMapping.primary_tool === tool
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium"
                          : "border-slate-200 hover:border-slate-300 text-slate-600"
                      }`}>
                      {tool}
                    </button>
                  ))}
                  <button onClick={() => updateMapping("primary_tool", "Other")}
                    className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${
                      currentMapping.primary_tool === "Other"
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 font-medium"
                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                    }`}>
                    Other
                  </button>
                </div>
              </div>

              {/* Shares Data With */}
              <div className="mb-5">
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Shares Data With (Select departments)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {departments.filter(d => d.key !== currentDept.key).map(d => {
                    const selected = (currentMapping.shares_data_with || []).includes(d.key);
                    return (
                      <button key={d.key} onClick={() => toggleShareWith(d.key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          selected
                            ? "border-green-400 bg-green-50 text-green-700"
                            : "border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}>
                        {selected && <CheckCircle className="w-3 h-3 inline mr-1" />}
                        {d.name.split(" ")[0]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sharing Method */}
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">How is data shared?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {toolLibrary.sharing_methods.map(method => {
                    const style = CONNECTION_STYLES[method] || {};
                    return (
                      <button key={method} onClick={() => updateMapping("sharing_method", method)}
                        className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${
                          currentMapping.sharing_method === method
                            ? "font-medium"
                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                        style={currentMapping.sharing_method === method ? { borderColor: style.color, backgroundColor: `${style.color}10`, color: style.color } : {}}>
                        {method}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <Button variant="outline" disabled={currentDeptIdx === 0} onClick={() => setCurrentDeptIdx(p => p - 1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            {isLast ? (
              <Button onClick={() => setStep("contact")} className="bg-indigo-600 hover:bg-indigo-700">
                Review & Submit <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={() => setCurrentDeptIdx(p => p + 1)} style={{ backgroundColor: color }}>
                Next Department <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CONTACT
  if (step === "contact") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 pb-16 max-w-lg mx-auto px-4">
          <button onClick={() => setStep("mapper")} className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to mapper
          </button>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Almost there!</h2>
          <p className="text-slate-500 text-sm mb-6">Enter your details to see your System Landscape Report.</p>
          <Card>
            <CardContent className="p-6 space-y-4">
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Company Name *</Label>
                <Input className="placeholder:text-slate-300" value={contactInfo.company_name} onChange={e => setContactInfo(p => ({...p, company_name: e.target.value}))} placeholder="e.g. ABC Manufacturing Pvt Ltd" data-testid="mapper-company" /></div>
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Industry</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={contactInfo.industry} onChange={e => setContactInfo(p => ({...p, industry: e.target.value}))}>
                  <option value="">Select Industry</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail & Distribution</option>
                  <option value="FMCG">FMCG / Consumer Goods</option>
                  <option value="Textile">Textile & Apparel</option>
                  <option value="Pharma">Pharma & Healthcare</option>
                  <option value="Other">Other</option>
                </select></div>
              <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Your Name *</Label>
                <Input className="placeholder:text-slate-300" value={contactInfo.contact_name} onChange={e => setContactInfo(p => ({...p, contact_name: e.target.value}))} placeholder="e.g. Rajesh Kumar" data-testid="mapper-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Email *</Label>
                  <Input className="placeholder:text-slate-300" type="email" value={contactInfo.contact_email}
                    onChange={e => setContactInfo(p => ({...p, contact_email: e.target.value}))}
                    onBlur={e => loadHistory(e.target.value)}
                    placeholder="e.g. you@company.com" data-testid="mapper-email" /></div>
                <div><Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</Label>
                  <Input className="placeholder:text-slate-300" value={contactInfo.contact_phone} onChange={e => setContactInfo(p => ({...p, contact_phone: e.target.value}))} placeholder="e.g. +91 98765 43210" /></div>
              </div>

              {historyLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Checking previous mappings...</div>}
              {history.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800">
                    <History className="w-4 h-4" /> Previous Mappings ({history.length}/3)
                  </div>
                  {history.map((h, i) => (
                    <div key={h.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-indigo-100">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{h.company_name} — Score: {h.calculated_metrics?.integration_score}/100</p>
                        <p className="text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => downloadReport(h)}>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    </div>
                  ))}
                  {maxReached && (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                      <AlertCircle className="w-4 h-4" /> Maximum 3 mappings reached. Download your reports above.
                    </div>
                  )}
                </div>
              )}

              <Button className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" disabled={loading || maxReached} onClick={handleSubmit} data-testid="mapper-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Network className="w-4 h-4 mr-2" />}
                {maxReached ? "Maximum Attempts Reached" : "Generate My IT Landscape Report"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // RESULTS
  if (step === "results" && result) {
    const metrics = result.calculated_metrics || {};
    const deptMap = {};
    (result.departments || []).forEach(d => { deptMap[d.department_key] = d; });

    const scoreColor = metrics.integration_score >= 60 ? "#10B981" : metrics.integration_score >= 30 ? "#F59E0B" : "#EF4444";
    const scoreLabel = metrics.integration_score >= 60 ? "Well Connected" : metrics.integration_score >= 30 ? "Partially Connected" : "Heavily Siloed";

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <div className="pt-24 pb-16 max-w-5xl mx-auto px-4 flex-1">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900" data-testid="mapper-results-title">Your IT Landscape Report</h1>
            <p className="text-slate-500 mt-1">{result.company_name}</p>
          </div>

          {/* Integration Score */}
          <Card className="mb-8 border-2" style={{ borderColor: `${scoreColor}40` }}>
            <CardContent className="p-8 text-center">
              <div className="text-6xl font-extrabold" style={{ color: scoreColor }} data-testid="integration-score">{metrics.integration_score}</div>
              <div className="text-slate-400 text-sm mt-1">out of 100</div>
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-white border rounded-full" style={{ borderColor: `${scoreColor}40` }}>
                <Network className="w-4 h-4" style={{ color: scoreColor }} />
                <span className="text-sm font-semibold text-slate-800">{scoreLabel}</span>
              </div>
              <div className="mt-4">
                <Button variant="outline" onClick={() => downloadReport()} data-testid="download-mapper-report">
                  <Download className="w-4 h-4 mr-2" /> Download Report
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Metrics Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[
              { label: "Total Systems in Use", value: metrics.total_tools, benchmark: "Ideal: 1-2 integrated platforms", bad: metrics.total_tools > 5 },
              { label: "Standalone Departments", value: `${metrics.standalone_departments}/10`, benchmark: "Should be: 0", bad: metrics.standalone_departments > 3 },
              { label: "Integration Connections", value: `${metrics.integration_connections}/${metrics.max_possible_connections}`, benchmark: "Should be: 30+", bad: metrics.integration_connections < 10 },
              { label: "WhatsApp/Verbal Sharing", value: `${metrics.whatsapp_verbal_count} depts`, benchmark: "Should be: 0", bad: metrics.whatsapp_verbal_count > 0 },
              { label: "Single Source of Truth", value: metrics.has_single_source ? "Yes" : "No", benchmark: "Should be: YES", bad: !metrics.has_single_source },
              { label: "Integration Score", value: `${metrics.integration_score}/100`, benchmark: "Industry Avg: 45", bad: metrics.integration_score < 45 },
            ].map((m, i) => (
              <Card key={i} className="border border-slate-200">
                <CardContent className="p-5">
                  <div className="text-xs text-slate-500 mb-1">{m.label}</div>
                  <div className={`text-2xl font-bold ${m.bad ? "text-red-600" : "text-green-600"}`}>{m.value}</div>
                  <div className="text-xs text-slate-400 mt-1">{m.benchmark}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Department Map Visualization */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Your Department Landscape</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {(result.departments || []).map(d => {
                  const Icon = DEPT_ICONS[d.department_key] || Monitor;
                  const color = DEPT_COLORS[d.department_key];
                  const connStyle = CONNECTION_STYLES[d.sharing_method] || {};
                  const deptInfo = departments.find(x => x.key === d.department_key);
                  return (
                    <div key={d.department_key} className="p-4 rounded-xl border border-slate-200 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                          <Icon className="w-4 h-4" style={{ color }} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-800">{deptInfo?.name || d.department_key}</div>
                          <div className="text-xs font-medium" style={{ color }}>{d.primary_tool}</div>
                        </div>
                      </div>
                      {d.shares_data_with?.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap mt-2">
                          <ArrowRightLeft className="w-3 h-3" style={{ color: connStyle.color || "#94A3B8" }} />
                          <span className="text-xs" style={{ color: connStyle.color }}>{d.sharing_method}:</span>
                          {d.shares_data_with.map(sw => (
                            <span key={sw} className="text-xs bg-slate-100 px-2 py-0.5 rounded capitalize">{sw}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 mt-2 text-red-500">
                          <XCircle className="w-3 h-3" />
                          <span className="text-xs">No data sharing — isolated silo</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Auto-generated Problems */}
          {result.auto_problems?.length > 0 && (
            <Card className="mb-8 border border-red-200 bg-red-50/30">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" /> Problems Identified in Your Landscape
                </h3>
                <div className="space-y-3">
                  {result.auto_problems.map((p, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-white rounded-lg border border-red-100">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-red-700">{i + 1}</span>
                      </div>
                      <p className="text-sm text-slate-700">{p}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Architecture */}
          <Card className="mb-8 border border-green-200 bg-green-50/30">
            <CardContent className="p-6 text-center">
              <h3 className="text-lg font-bold text-slate-900 mb-4">What Your Landscape Should Look Like</h3>
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 rounded-full bg-green-100 border-4 border-green-400 flex items-center justify-center mx-auto mb-4">
                  <span className="text-sm font-bold text-green-800">Unified<br/>ERP</span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {departments.map(d => {
                    const Icon = DEPT_ICONS[d.key] || Monitor;
                    return (
                      <div key={d.key} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-green-200 rounded-full">
                        <Icon className="w-3 h-3 text-green-600" />
                        <span className="text-xs text-green-700">{d.name.split(" ")[0]}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-slate-600 mt-4">All departments connected through a single ERP platform. One data entry, everywhere available.</p>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="border-2 border-indigo-200 bg-indigo-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Ready to connect the dots?</h3>
              <p className="text-slate-600 text-sm mb-6 max-w-md mx-auto">
                An integrated ERP replaces all disconnected systems with one platform. Let us show you how.
              </p>
              <Button className="bg-[#0066CC] hover:bg-[#0055aa] px-8 h-11" data-testid="mapper-book-call-btn"
                onClick={() => window.open("https://calendly.com", "_blank")}>
                Get a Free Integration Assessment <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
        <AbsolinDisclaimer />
        <Footer />
      </div>
    );
  }

  return null;
}
